<template>
  <div v-loading="btnLoading" :class="{ loading: btnLoading }"><slot></slot><input v-show="!btnLoading" type="file" :accept="accept" title="" @change="handleSelectedFile" /></div>
</template>

<script setup>
import { getOssCredentials, ossTmpUpload } from '@/api/updf-app';
import { formatFileSize, isArray } from '@/libs/tools';
import { getFileBlobUrl, getFileHash } from '@/libs/utils'
import { ElMessage } from 'element-plus';
import { ref } from 'vue';
import OSS from 'ali-oss'
import config from '@/config';
import { computed } from 'vue';
// 该组件上传一些普通文件，比如反馈意见的文件上传
const props = defineProps({
  // 允许上传的文件类型，多个类型可使用数组，如：[acceptFileTypeEnums.pdf, acceptFileTypeEnums.image]
  acceptType: {
    type: String || Array,
    default: 'all'
  },
  // 允许上传的文件类型后缀数组，如果该字段不为空，则会覆盖acceptType
  acceptExts: {
    type: Array,
    default: () => []
  },
  fileType: {
    type: String,
    default: 'tmp'
  },
  dir: {
    type: String,
    default: 'feedback'
  },
  uploadedFiles: { // 已上传的文件列表，可以防止重复选择同一张图片，如果允许重复选择，则不传该参数即可
    type: Array,
    default: () => [],
  }
})
const emits = defineEmits(['on-select', 'on-progress', 'on-success', 'on-error'])

const acceptFileTypeExtEnums = {
  pdf: ['pdf','PDF'],
  image: ['png','PNG','jpg','JPG','jpeg','JPEG','webp','WEBP', 'bmp', 'gif', 'tif', 'tiff'],
  other: ['txt', 'TXT'],
  all: []
}
acceptFileTypeExtEnums.all = [...acceptFileTypeExtEnums.pdf, ...acceptFileTypeExtEnums.image, ...acceptFileTypeExtEnums.other]

const btnLoading = ref(false)
// 允许上传的文件后缀数组
const acceptExts = computed(() => {
  if (props.acceptExts.length) {
    return props.acceptExts
  }
  if (isArray(props.acceptType)) {
    const res = []
    props.acceptType.forEach(type => {
      res.push(...acceptFileTypeExtEnums[type])
    })
    return res
  }
  return acceptFileTypeExtEnums[props.acceptType]
})
// 允许上传的文件后缀逗号拼接后的字符串
const accept =  computed(() => acceptExts.value?.map(ext => `.${ext}`).join(',')) 
const credentials = {}

const handleSelectedFile = async event => {
  const localFileData = event.target.files[0]
  event.target.value = ''
  const fileExt = localFileData.name.split('.').pop()
  if (!acceptExts.value.includes(fileExt)) {
    ElMessage.error($t('Upload of this file type is not supported.'))
    return false
  }
  const getOssCredentialsBody = {
    fileType: props.fileType
  }
  const maxFileSize = 2 * 1024 * 1024 * 1024
  if (localFileData.size > maxFileSize) {
    ElMessage.error(sprintf($t('Your file is too large, the maximum file size is %s.'), formatFileSize(maxFileSize)))
    return false
  }
  btnLoading.value = true
  emits('on-select', localFileData)
  const hash = await getFileHash(localFileData)
  if (props.uploadedFiles.filter(item => item.hash === hash).length) {
    btnLoading.value = false
    return false
  }
  try {
    // 如果没有凭证或者凭证已过期，先获取凭证
    if (!credentials.accessKeyId || credentials.expiration * 1000 < new Date().getTime()) {
      const { data } = await getOssCredentials(getOssCredentialsBody)
      Object.assign(credentials, data)
    }
    const regionData = credentials.endpoint.includes('oss-cn-hangzhou') ? config.ossApp['cn-east'] : config.ossApp['us-east']
    const ossClient = new OSS({
      // yourRegion填写Bucket所在地域。以华东1（杭州）为例，Region填写为oss-cn-hangzhou。
      region: regionData.region,
      // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
      accessKeyId: credentials.accessKeyId,
      accessKeySecret: credentials.accessKeySecret,
      // 从STS服务获取的安全令牌（SecurityToken）。
      stsToken: credentials.securityToken,
      // 刷新临时访问凭证的时间间隔，单位为毫秒。
      refreshSTSTokenInterval: 3600 * 1000,
      refreshSTSToken: async () => {
      // 向您搭建的STS服务获取临时访问凭证。
        const { data } = await getOssCredentials(getOssCredentialsBody)
        return {
          accessKeyId: data.accessKeyId,
          accessKeySecret: data.accessKeySecret,
          stsToken: data.securityToken
        }
      },
      // 填写Bucket名称。
      bucket: credentials.bucketName
    })
    // 设置分片上传参数
    const options = {
      // 获取分片上传进度、断点和返回值。
      progress: (p, cpt, res) => {
        const currentProgress = parseInt(p * 100)
        emits('on-progress', currentProgress)
        console.log(p, cpt, res)
      },
      // 设置并发上传的分片数量。
      parallel: 4,
      // 设置分片大小。默认值为1 MB，最小值为100 KB。
      partSize: 1024 * 1024,
      // headers,
      // 自定义元数据，通过HeadObject接口可以获取Object的元数据。
      // meta: { year: 2020, people: "test" },
      mime: localFileData.type,
    }
    const { data: fileTmpData } = await ossTmpUpload({ dir: props.dir, fileName: localFileData.name })
    const uploadRes = await ossClient.multipartUpload(fileTmpData.remotePath, localFileData, options)
    // const fileUrl = await ossClient.signatureUrl(fileTmpData.remotePath)
    const fileUrl = await getFileBlobUrl(localFileData)
    const fileExt = localFileData.name.split('.').pop().toLowerCase()
    emits('on-success', { url: fileUrl, size: localFileData.size, name: localFileData.name, ext: fileExt, hash, ...fileTmpData })
  } catch (e) {
    console.log(e)
  }
  btnLoading.value = false
}
</script>

<style lang="scss" scoped>
div {
  position: relative;
  &.loading {
    cursor: default!important;
  }
  input {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    opacity: 0;
    cursor: pointer;
    &::-webkit-file-upload-button{
      cursor: pointer;
    }
  }
  :deep(.el-loading-mask) {
    border-radius: inherit;
    .el-loading-spinner {
      .circular {
        width: 20px;
      }
    }
  }
}
</style>
